import { Component, EventEmitter, HostBinding, Input, Output, ViewChild, OnChanges, SimpleChanges, AfterViewInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CdkTableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { TableColmuns } from 'src/app/interface/TableColumnsinterface';
import { SelectionModel } from '@angular/cdk/collections';
import { PlanificadorService } from '../../models/control.service';
import { Model } from '../../../models/model';

export interface ResultRow {
  type: string;
  condition?: string;
  conditions?: string[];
  compoundRate: number;
  maxDrawdown: number;
  maxDrawdownObjetivo: number;
  maxDrawdownValor: number;
  beneficio: number;
  operaciones: number;
  score: number;
  group: number;
  tiporiesgo: string;
  fracciones: boolean;
  modelMeetsCriteria?: string;
  valorriesgo?: number;
  stopLoss?: number;
  profit?: number;
  diasSalida?: number;
  stoplossActivo?: boolean;
  profitActivo?: boolean;
  diasSalidaActivo?: boolean;
  stopLossDinamicActivo?: boolean;
  //controlMarginCall?: number;
  //margenRequeridoPor: number;
  //riesgoMaximo?: number;
  //tipoCuenta?: string;
}

@Component({
  selector: 'app-strategy-factory-results-table',
  templateUrl: './results-table.component.html',
  styleUrls: ['./results-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategyFactoryResultsTableComponent implements OnChanges, AfterViewInit, OnDestroy {
  panelOpenState = false;
  colPanelOpen = false;
  expandirColPanel() { this.colPanelOpen = true; }
  contraerColPanel() { this.colPanelOpen = false; }

  // Desplaza el hotspot del panel flotante para que no se solape con las columnas
  // "match"/"select" (interactivas), que si no quedan tapadas por el botón de 40x40.
  get colPanelLeftOffset(): number {
    let offset = 0;
    if (this.showMatchColumn) offset += 48; // ancho columna "match"
    if (this.enableSelection && this.selection) offset += 50; // ancho columna "select"
    return offset;
  }

  @ViewChild(CdkVirtualScrollViewport, { static: false }) viewport!: CdkVirtualScrollViewport;
  
  @Input() title: string | null = null;
  @Input() data: ResultRow[] = [];
  @Input() enableContextMenu = false;
  @Input() contextSource: 'main' | 'optimized' | 'optimized2' | 'optimized3' | 'reevaluacion' = 'main';
  @Input() showModelMeetsCriteria = true;
  @Input() showMatchColumn = false; // show external match/id column (visual only)
  @Input() matchIdFn?: (row: any) => number | null; // function provided by parent to compute match id (use any to avoid strict cross-component type issues)
  @Input() selectedRow?: ResultRow;
  @Input() capitalInicial: number | null = null;
  @Input() valorRiesgo: number | null = null;
  @Input() tipoCuenta: string = 'MARGEN';
  @Input() margenRequeridoPor: number | null = 30;
  @Input() controlMarginCall: number | null = 75;
  @Input() riesgoMaximo: number | null = 50;
  @Input() comisionBroker: number | null = null;
  @Input() gastosGestionPor: number | null = null;
  @Input() gastosPrestamoPor: number | null = null;
  @Input() chkComisionBroker: boolean | null = null;
  @Input() chkGastoGest: boolean | null = null;
  @Input() chkGastoPrest: boolean | null = null;
  @Input() height: string = '100%'; // Por defecto usa todo el alto disponible del contenedor
  @Input() enableSelection = false; // Habilitar checkboxes de selección
  @Input() selection?: SelectionModel<any>; // Modelo de selección desde el componente padre
  @Input() tabId?: number;

  @Output() rowContextMenu = new EventEmitter<{ event: MouseEvent; row: ResultRow; source: 'main' | 'optimized' | 'optimized2' | 'optimized3' | 'reevaluacion' }>();
  @Output() selectionChange = new EventEmitter<void>();
  /** Clic en el título oculta/muestra la tabla (pestañas con dos tablas). */
  @Input() plegable = false;
  @Input() @HostBinding('class.rt-host-plegada') plegada = false;
  @Output() plegadaChange = new EventEmitter<boolean>();

  togglePlegada(): void {
    if (!this.plegable) return;
    this.plegada = !this.plegada;
    this.plegadaChange.emit(this.plegada);
    // Al volver a mostrarse el viewport virtual necesita recalcular su tamaño
    if (!this.plegada) this.refreshViewport();
  }

  // Local sorting state
  sortColumn: string = 'tasaMDRatio';
  sortDirection: 'asc' | 'desc' = 'desc';

  // CDK Table Data Source
  cdkTableData: CdkTableVirtualScrollDataSource<ResultRow>;

  // Debounce para controlar actualizaciones rápidas del SSE
  private updateSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private pendingData: ResultRow[] = [];
  
  // Para rastrear cambios en el tamaño de la data
  private previousDataLength: number = 0;

  // Table Columns Configuration
  tableColumns: TableColmuns[] = [
    {id: 'index', name: '#', isShow: true, isDate: false, isNumber: false, isString: true},
    {id: 'type', name: 'Tipo', isShow: true, isDate: false, isNumber: false, isString: true},
    {id: 'condition', name: 'Condición', isShow: true, isDate: false, isNumber: false, isString: true},
    {id: 'compoundRate', name: 'Tasa Compuesta', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'beneficio', name: 'Beneficio $', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'maxDrawdownValor', name: 'Drawdown $', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'maxDrawdown', name: 'Máx. Drawdown', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'maxDrawdownObjetivo', name: 'Máx. Drawdown Objetivo', isShow: false, isDate: false, isNumber: true, isString: false},
    {id: 'stopLoss', name: 'Stop Loss', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'profit', name: 'Profit', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'diasSalida', name: 'Días Salida', isShow: false, isDate: false, isNumber: true, isString: false},
    {id: 'valorriesgo', name: 'Valor Riesgo', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'tasaMDRatio', name: 'Tasa/M.D.', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'operaciones', name: 'Operaciones', isShow: true, isDate: false, isNumber: true, isString: false},
    {id: 'score', name: 'Puntuación', isShow: false, isDate: false, isNumber: true, isString: false},
    {id: 'modelMeetsCriteria', name: 'Criterios', isShow: false, isDate: false, isNumber: false, isString: true}
  ];

  displayedColumns: string[] = [];

  constructor(private cdr: ChangeDetectorRef, private planificadorsv: PlanificadorService) {
    this.cdkTableData = new CdkTableVirtualScrollDataSource<ResultRow>();
    this.updateDisplayedColumns();
    
    // Configurar throttle de 500ms - permite primera actualización inmediata, luego limita las siguientes
    this.updateSubject.pipe(
      throttleTime(500, undefined, { leading: true, trailing: true }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyDataUpdate();
    });
  }

  // Preview state for grafica popover
  public previewVisible: boolean = false;
  public previewLeft: number = 0;
  public previewTop: number = 0;
  public previewRow: ResultRow | null = null;
  // Timers and delays for hover behavior
  private previewOpenTimer: any = null;
  private executeTriggerTimer: any = null;
  private previewCloseTimer: any = null;
  private hoverDelayMs: number = 180; // wait before opening preview
  private executeDelayMs: number = 60; // wait after isshow before triggering execute

  showPreview(event: MouseEvent, row: ResultRow) {
    // Update position immediately
    this.previewLeft = event.clientX + 12;
    this.previewTop = event.clientY + 12;

    // If same row is already previewed, just update coords
    if (this.previewRow && this.previewRow.group === row.group && this.previewVisible) {
      this.cdr.markForCheck();
      return;
    }

    // Clear any previous timers
    if (this.previewOpenTimer) {
      clearTimeout(this.previewOpenTimer);
      this.previewOpenTimer = null;
    }
    if (this.executeTriggerTimer) {
      clearTimeout(this.executeTriggerTimer);
      this.executeTriggerTimer = null;
    }
    // If a close timer was scheduled (leaving towards the popover), cancel it
    if (this.previewCloseTimer) {
      clearTimeout(this.previewCloseTimer);
      this.previewCloseTimer = null;
    }

    // Schedule opening the preview after a short hover delay
    this.previewOpenTimer = setTimeout(() => {
      this.previewRow = row;
      this.previewVisible = true;
      console.log('Showing preview for model group:', row.group);
      // Build a minimal Model for grafica to consume via PlanificadorService
      
      const model = new Model(
        Number(row.group ?? 0), // idModelo
        0, // idMercado
        '', // fromDate
        '', // rangoTiempo
        '', // Nombre
        '', // guardado
        0, // tipo
        -1, // periodoModeloPeriodico
        '', // condicion
        '', // fechaInicio
        false, // bajista
        0, // stoploss
        0, // periodo
        0, // porcentajePrima
        Number(row.valorriesgo ?? 1000)      
      );

      try {
        // First publish the model (single object) and mark isshow so grafica has its Modelformdata
        this.planificadorsv.updateMod(model as any);
        this.planificadorsv.updateaisshoW(true);

        // After a tiny delay trigger executeGrap so grafica actually fetches data
        this.executeTriggerTimer = setTimeout(() => {
          try { this.planificadorsv.updateExecuteGrap(true); } catch (e) { }
        }, this.executeDelayMs);
      } catch (e) {
        // ignore
      }

      this.cdr.markForCheck();
    }, this.hoverDelayMs);
  }

  hidePreview() {
    // Clear timers
    if (this.previewOpenTimer) { clearTimeout(this.previewOpenTimer); this.previewOpenTimer = null; }
    if (this.executeTriggerTimer) { clearTimeout(this.executeTriggerTimer); this.executeTriggerTimer = null; }

    this.previewVisible = false;
    this.previewRow = null;
    try {
      this.planificadorsv.updateaisshoW(false);
      this.planificadorsv.updateMod([]);
    } catch (e) { }
    this.cdr.markForCheck();
  }

  // Keep the preview open when pointer enters the popover
  keepPreview() {
    if (this.previewCloseTimer) {
      clearTimeout(this.previewCloseTimer);
      this.previewCloseTimer = null;
    }
    this.previewVisible = true;
    this.cdr.markForCheck();
  }

  // Delay hiding to allow pointer to move from link to popover
  hidePreviewDelayed(delayMs: number = 200) {
    if (this.previewCloseTimer) {
      clearTimeout(this.previewCloseTimer);
    }
    this.previewCloseTimer = setTimeout(() => {
      this.hidePreview();
      this.previewCloseTimer = null;
    }, delayMs);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.previewCloseTimer) { clearTimeout(this.previewCloseTimer); this.previewCloseTimer = null; }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //console.log('ngOnChanges called - changes:', changes);
    if (changes['data']) {
      //console.log('data change detected - previous:', changes['data'].previousValue?.length, 'current:', changes['data'].currentValue?.length);
      //console.log('this.data:', this.data);
      // Usar debounce para evitar actualizaciones demasiado rápidas
      this.updateSubject.next();
    }
    if (changes['showModelMeetsCriteria']) {
      this.updateDisplayedColumns();
    }
    // React to showMatchColumn or matchIdFn changes to update displayed columns
    if (changes['showMatchColumn'] || changes['matchIdFn']) {
      this.updateDisplayedColumns();
    }
    // React when parent toggles selection inputs so checkbox column stays in sync
    if (changes['enableSelection'] || changes['selection']) {
      this.updateDisplayedColumns();
    }
  }

  ngAfterViewInit(): void {
    // Initial data load if data is already present
    if (this.data && this.data.length > 0) {
      this.applyDataUpdate();
    }
  }

  // Aplicar la actualización de datos (llamada después del debounce)
  private applyDataUpdate(): void {
    const data = this.sortedData;

    // sortedData crea nuevos objetos vía spread ({...row, index}), rompiendo las referencias
    // almacenadas en el SelectionModel. Re-sincronizamos usando group+valorriesgo como clave estable,
    // tocando solo los items que pertenecen a ESTA tabla para no afectar otros app-strategy-factory-results-table
    // que comparten el mismo SelectionModel.
    if (this.selection && this.enableSelection && data.length > 0) {
      const thisTableKeys = new Set(data.map((r: any) => `${r.group}_${r.valorriesgo}`));
      const prevInThisTable = this.selection.selected.filter((r: any) =>
        thisTableKeys.has(`${r.group}_${r.valorriesgo}`)
      );
      if (prevInThisTable.length > 0) {
        const selectedKeys = new Set(prevInThisTable.map((r: any) => `${r.group}_${r.valorriesgo}`));
        this.selection.deselect(...prevInThisTable);
        const toReselect = data.filter((r: any) => selectedKeys.has(`${r.group}_${r.valorriesgo}`));
        if (toReselect.length > 0) {
          this.selection.select(...toReselect);
        }
      }
    }

    // Se reutiliza el dataSource (en vez de crear uno nuevo) para no perder la posición del scroll
    // cada vez que llega un resultado durante la ejecución.
    this.cdkTableData.data = data;
    this.previousDataLength = data.length;

    // Forzar detección de cambios
    this.cdr.detectChanges();

    // Forzar actualización del viewport
    setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
        this.cdr.detectChanges();
      }
    }, 50);
  }

  updateDataSource(): void {
    // Forzar actualización inmediata (usado para ordenamiento manual)
    this.applyDataUpdate();
  }

  /** Texto completo de la condición (tooltip de la celda, ya que la fila es de una sola línea). */
  textoCondicionCompleto(item: any): string {
    if (item?.type === 'Combination') {
      return (item.conditions || []).map((c: string, j: number) => `${j + 1}. ${c}`).join('\n');
    }
    return item?.condition || '';
  }

  /** Refresco síncrono del viewport (arrastre del divisor de alto en el padre). */
  actualizarViewport(): void {
    if (this.viewport) {
      this.viewport.checkViewportSize();
      this.cdr.detectChanges();
    }
  }

  // Método para refrescar el viewport cuando el componente se hace visible
  refreshViewport(): void {
    setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
        this.cdr.detectChanges();
      }
    }, 100);
  }

  updateDisplayedColumns(): void {
    const baseColumns = this.tableColumns
      .filter(col => col.isShow && (col.id !== 'modelMeetsCriteria' || this.showModelMeetsCriteria))
      .map(col => col.id);
    // Agregar columna de selección al inicio si enableSelection es true
    if (this.enableSelection && this.selection) {
      this.displayedColumns = ['select', ...baseColumns];
    } else {
      this.displayedColumns = baseColumns;
    }

    // Agregar columna visual de match al inicio si está habilitada
    if (this.showMatchColumn) {
      // Evitar duplicados
      if (!this.displayedColumns.includes('match')) {
        this.displayedColumns = ['match', ...this.displayedColumns];
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'match');
    }
  }

  toggleColumnVisibility(id: any) {
    const column = this.tableColumns.find(col => col.id === id);
    if (column) {
      column.isShow = !column.isShow;
      this.updateDisplayedColumns();
    }
  }

  onRowContextMenu(event: MouseEvent, row: ResultRow) {
    if (!this.enableContextMenu) return;
    this.rowContextMenu.emit({ event, row, source: this.contextSource });
  }

  sortBy(column: string) {
    if (column === 'index' || column === 'modelMeetsCriteria' || column === 'match') {
      return; // Don't sort by these columns
    }
    
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    
    // Update dataSource after sorting
    this.updateDataSource();
  }

  // Compute a sorted view without mutating the @Input() array
  get sortedData(): any[] {
    const rows = this.data || [];
    let sortedRows = rows;
    
    if (this.sortColumn) {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      sortedRows = [...rows].sort((a, b) => {
      let comparison = 0;
      switch (this.sortColumn as any) {
        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;
        case 'condition':
          comparison = this.getConditionText(a).localeCompare(this.getConditionText(b));
          break;
        case 'compoundRate':
          comparison = (a.compoundRate || 0) - (b.compoundRate || 0);
          break;
        case 'maxDrawdown':
          comparison = (a.maxDrawdown || 0) - (b.maxDrawdown || 0);
          break;
        case 'maxDrawdownObjetivo':
          comparison = (a.maxDrawdownObjetivo || 0) - (b.maxDrawdownObjetivo || 0);
          break;
        case 'beneficio':
          comparison = (a.beneficio || 0) - (b.beneficio || 0);
          break;
        case 'maxDrawdownValor':
          comparison = (a.maxDrawdownValor || 0) - (b.maxDrawdownValor || 0);
          break;
        case 'valorriesgo':
          comparison = (a.valorriesgo || 0) - (b.valorriesgo || 0);
          break;
        case 'operaciones':
          comparison = (a.operaciones || 0) - (b.operaciones || 0);
          break;
        case 'score':
          comparison = (a.score || 0) - (b.score || 0);
          break;
        case 'stopLoss':
          comparison = (a.stopLoss || 0) - (b.stopLoss || 0);
          break;
        case 'profit':
          comparison = (a.profit || 0) - (b.profit || 0);
          break;
        case 'diasSalida':
          comparison = (a.diasSalida || 0) - (b.diasSalida || 0);
          break;
        case 'tasaMDRatio':
          const aRatio = this.getTasaMDRatio(a);
          const bRatio = this.getTasaMDRatio(b);
          // Poner los 0 (y los ratios infinitos, ya normalizados a 0) siempre al final, sin importar la dirección de ordenamiento
          if (aRatio === 0 && bRatio !== 0) {
            return 1; // a va después de b (al final)
          } else if (aRatio !== 0 && bRatio === 0) {
            return -1; // b va después de a (al final)
          } else {
            comparison = aRatio - bRatio;
          }
          break;
      }
      return comparison * dir;
      });
    }
    
    // Add index to each row for display
    return sortedRows.map((row, index) => ({
      ...row,
      index: index + 1
    }));
  }

  getModelLink(row: ResultRow): string {
    const params = new URLSearchParams();
    params.set('id', String(row.group ?? ''));
    params.set('ia', 'true');
    if (this.capitalInicial !== null && this.capitalInicial !== undefined) {
      params.set('capitalinicial', String(this.capitalInicial));
    }
    if (row.valorriesgo !== null && row.valorriesgo !== undefined) {
      params.set('valorriesgo', String(row.valorriesgo));
    }
    params.set('tiporiesgo', row.tiporiesgo);
    params.set('fracciones', row.fracciones ? 'true' : 'false');
    // Agregar los 4 nuevos parámetros
    if (this.tipoCuenta) {
      params.set('tipoCuenta', this.tipoCuenta);
    }
    if (this.margenRequeridoPor !== null && this.margenRequeridoPor !== undefined) {
      params.set('margenRequeridoPor', String(this.margenRequeridoPor));
    }
    if (this.controlMarginCall !== null && this.controlMarginCall !== undefined) {
      params.set('controlMarginCall', String(this.controlMarginCall));
    }
    if (this.riesgoMaximo !== null && this.riesgoMaximo !== undefined) {
      params.set('riesgoMaximo', String(this.riesgoMaximo));
    }
    // Comisión y gastos: se arrastran para que /models parta de los mismos costes con los
    // que se simuló la estrategia y no de los valores por defecto de la cartera. Se pasa
    // también el estado de cada toggle, porque "valor 10 con el coste apagado" es un estado
    // legítimo que no se puede deducir del número.
    if (this.comisionBroker !== null && this.comisionBroker !== undefined) {
      params.set('comisionbroker', String(this.comisionBroker));
    }
    if (this.gastosGestionPor !== null && this.gastosGestionPor !== undefined) {
      params.set('gastosGestionPor', String(this.gastosGestionPor));
    }
    if (this.gastosPrestamoPor !== null && this.gastosPrestamoPor !== undefined) {
      params.set('gastosPrestamoPor', String(this.gastosPrestamoPor));
    }
    if (this.chkComisionBroker !== null && this.chkComisionBroker !== undefined) {
      params.set('chkcomisionbroker', this.chkComisionBroker ? 'true' : 'false');
    }
    if (this.chkGastoGest !== null && this.chkGastoGest !== undefined) {
      params.set('chkgastogest', this.chkGastoGest ? 'true' : 'false');
    }
    if (this.chkGastoPrest !== null && this.chkGastoPrest !== undefined) {
      params.set('chkgastoprest', this.chkGastoPrest ? 'true' : 'false');
    }
    return `/models?${params.toString()}`;
  }

  // Métodos para manejo de selección de checkboxes
  isAllSelected(): boolean {
    if (!this.selection) return false;
    const numSelected = this.selection.selected.length;
    const numRows = this.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    if (!this.selection) return;
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.data.forEach(row => this.selection!.select(row));
    }
    console.log('results-table: masterToggle emitted, selected count=', this.selection.selected.length, 'contextSource=', this.contextSource);
    this.selectionChange.emit();
  }

  toggleRow(row: ResultRow): void {
    if (!this.selection) return;
    this.selection.toggle(row);
    console.log('results-table: toggleRow emitted for row.group=', row.group, 'selected count=', this.selection.selected.length, 'contextSource=', this.contextSource);
    this.selectionChange.emit();
  }

  getAbsValue(value: number | null | undefined): number {
    return Math.abs(value ?? 0);
  }

  private getConditionText(row: any): string {
    return row.type === 'Combination'
      ? (row.conditions || []).join(' | ')
      : (row.condition || '');
  }

  getTasaMDRatio(item: { beneficio?: number; maxDrawdownValor?: number }): number {
    if (!item.beneficio) return 0;
    const ratio = item.beneficio / Math.abs(item.maxDrawdownValor ?? 0);
    return Number.isFinite(ratio) ? ratio : 0;
  }
}
